import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { ReferentMutation } from '../../../@types/Company';
import { refreshCompanyData } from '../../../store/AuthReducer';
import type { RootState } from '../../../store/store';
import { getValidReferents } from '../../../utils/branding';
import { JWTAuthorization } from '../../../utils/JWTHeader';
import FileUpload from '../FileUpload/FileUpload.module';
import { useFileUpload } from '../FileUpload/useFileUpload';
import { showToast } from '../Notification';
import Popup from '../Popup/Popup.module';
import Spinner from '../Spinner';
import ReferentPresentationUpload from './ReferentPresentationUpload';

const MAX_FILE_SIZE = 5;

type UploadMode = 'per-referent' | 'shared';

interface ReferentPresentationUploadListProps {
  onUploadSuccess?: () => void;
  onDeleteSuccess?: () => void;
  /** When false, hides the long intro paragraph (e.g. when parent already explains). */
  showIntro?: boolean;
}

const getReferentLabel = (referent: ReferentMutation): string => {
  const parts = [referent.title, referent.firstname, referent.lastname].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : referent.id;
};

const getReferentApiBase = (companyId: number, referentId: string): string => (
  `/api/companies/${companyId}/referents/${encodeURIComponent(referentId)}/report/settings/pdf-introduction`
);

const getPdfFetchUrl = (companyId: number, referentId: string): string => (
  `${getReferentApiBase(companyId, referentId)}?v=${Date.now()}`
);

const ReferentPresentationUploadList: React.FC<ReferentPresentationUploadListProps> = ({
  onUploadSuccess,
  onDeleteSuccess,
  showIntro = true,
}) => {
  const dispatch = useDispatch();
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const referents = useMemo(() => getValidReferents(company), [company]);

  const [uploadMode, setUploadMode] = useState<UploadMode>('per-referent');
  const [sharedFile, setSharedFile] = useState<File | null>(null);
  const [isLoadingShared, setIsLoadingShared] = useState(false);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);

  const referentsWithPdf = useMemo(
    () => referents.filter((referent) => Boolean(referent.pdfIntroductionName?.trim())),
    [referents],
  );

  const sharedSourceReferentId = referentsWithPdf[0]?.id ?? null;

  const refreshCompany = useCallback(async() => {
    if (!company?.id || !jwt) { return; }
    try {
      const response = await fetch(`/api/companies/${company.id}`, {
        headers: { Authorization: JWTAuthorization(jwt) },
      });
      if (response.ok) {
        const data = await response.json();
        dispatch(refreshCompanyData(data));
      }
    } catch (error) {
      console.error('Error refreshing company after referent PDF update:', error);
    }
  }, [company?.id, dispatch, jwt]);

  const handleUploadSuccess = useCallback(async() => {
    await refreshCompany();
    onUploadSuccess?.();
  }, [onUploadSuccess, refreshCompany]);

  const handleDeleteSuccess = useCallback(async() => {
    await refreshCompany();
    onDeleteSuccess?.();
  }, [onDeleteSuccess, refreshCompany]);

  useEffect(() => {
    if (uploadMode !== 'shared') {
      return;
    }

    if (!company?.id || !jwt || !sharedSourceReferentId) {
      setSharedFile(null);
      return;
    }

    let cancelled = false;
    setIsLoadingShared(true);

    fetch(getPdfFetchUrl(company.id, sharedSourceReferentId), {
      headers: { Authorization: JWTAuthorization(jwt) },
      cache: 'no-store',
    })
      .then(async(response) => {
        if (cancelled) { return; }
        if (response.ok) {
          const blob = await response.blob();
          setSharedFile(new File([blob], 'presentation-entreprise-rapports.pdf', { type: 'application/pdf' }));
        } else {
          setSharedFile(null);
        }
      })
      .catch((error) => {
        if (cancelled) { return; }
        console.error('Error fetching shared presentation PDF:', error);
        setSharedFile(null);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingShared(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [uploadMode, company?.id, jwt, sharedSourceReferentId]);

  const handleSharedUpload = async(file: File): Promise<boolean> => {
    if (!company?.id || !jwt || referents.length === 0) { return false; }

    setIsBatchProcessing(true);
    try {
      const results = await Promise.all(
        referents.map(async(referent) => {
          const formData = new FormData();
          formData.append('file', file);
          const response = await fetch(getReferentApiBase(company.id, referent.id), {
            method: 'POST',
            headers: { Authorization: JWTAuthorization(jwt) },
            body: formData,
          });
          return { referent, ok: response.ok };
        }),
      );

      const failures = results.filter((result) => !result.ok);
      if (failures.length > 0) {
        showToast(
          'error',
          `La présentation n'a pas pu être appliquée à ${failures.length} référent(s) sur ${referents.length}.`,
        );
        return false;
      }

      setSharedFile(file);
      showToast('success', `La présentation de l'entreprise a été appliquée à ${referents.length} référent(s).`);
      await handleUploadSuccess();
      return true;
    } catch (error) {
      console.error('Error batch uploading shared presentation:', error);
      showToast('error', 'Une erreur est survenue lors de l\'application de la présentation à tous les référents.');
      return false;
    } finally {
      setIsBatchProcessing(false);
    }
  };

  const handleSharedDelete = async() => {
    if (!company?.id || !jwt || referentsWithPdf.length === 0) { return; }

    setIsBatchProcessing(true);
    try {
      const results = await Promise.all(
        referentsWithPdf.map(async(referent) => {
          const response = await fetch(getReferentApiBase(company.id, referent.id), {
            method: 'DELETE',
            headers: { Authorization: JWTAuthorization(jwt) },
          });
          return response.ok;
        }),
      );

      if (results.some((ok) => !ok)) {
        showToast('error', 'La suppression n\'a pas pu être effectuée pour tous les référents.');
        return;
      }

      setSharedFile(null);
      showToast('success', 'La présentation de l\'entreprise a été supprimée pour tous les référents.');
      await handleDeleteSuccess();
    } catch (error) {
      console.error('Error batch deleting shared presentation:', error);
      showToast('error', 'Une erreur est survenue lors de la suppression de la présentation.');
    } finally {
      setIsBatchProcessing(false);
    }
  };

  const sharedFileUpload = useFileUpload({
    onFileSelect: handleSharedUpload,
    onFileDelete: handleSharedDelete,
    acceptedFileTypes: '.pdf',
    maxFileSize: MAX_FILE_SIZE,
    currentFile: sharedFile,
  });

  const handlePreviewSharedPdf = async() => {
    if (!company?.id || !jwt || referentsWithPdf.length === 0) { return; }

    try {
      const response = await fetch(getPdfFetchUrl(company.id, referentsWithPdf[0].id), {
        headers: { Authorization: JWTAuthorization(jwt) },
        cache: 'no-store',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch PDF');
      }
      const blob = await response.blob();
      setPdfPreviewUrl(URL.createObjectURL(blob));
      setIsPdfPreviewOpen(true);
    } catch (error) {
      console.error('Error previewing shared PDF:', error);
      showToast('error', 'Une erreur est survenue lors de la prévisualisation du PDF.');
    }
  };

  const closePdfPreview = () => {
    setIsPdfPreviewOpen(false);
    setPdfPreviewUrl(null);
  };

  if (!company?.id || !jwt) {
    return null;
  }

  if (referents.length === 0) {
    return (
      <p className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        Configurez au moins un référent mutation/location dans l&apos;image de marque avant d&apos;ajouter une présentation PDF.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {showIntro && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
          <p className="font-medium text-gray-800">Présentation de l&apos;entreprise pour les rapports</p>
          <p className="mt-2 leading-relaxed">
            Ce PDF constitue la première section de vos rapports générés (copropriété, DPE, ventes, valorisation).
            Il s&apos;agit d&apos;une présentation de votre entreprise, ajoutée automatiquement avant le contenu du rapport.
            Selon le référent publique assigné à chaque copropriété, la version correspondante sera utilisée.
          </p>
        </div>
      )}

      <div>
        <p className="mb-2 text-sm font-medium text-gray-700">Mode de configuration</p>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Mode de présentation des rapports">
          <button
            type="button"
            role="radio"
            aria-checked={uploadMode === 'per-referent'}
            onClick={() => setUploadMode('per-referent')}
            className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
              uploadMode === 'per-referent'
                ? 'border-rgpd-blue bg-rgpd-blue text-white shadow-sm'
                : 'border-gray-300 bg-white text-gray-600 hover:border-rgpd-blue hover:text-rgpd-blue'
            }`}
          >
            Un rapport par référent
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={uploadMode === 'shared'}
            onClick={() => setUploadMode('shared')}
            className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
              uploadMode === 'shared'
                ? 'border-rgpd-blue bg-rgpd-blue text-white shadow-sm'
                : 'border-gray-300 bg-white text-gray-600 hover:border-rgpd-blue hover:text-rgpd-blue'
            }`}
          >
            Un rapport pour tous les référents
          </button>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          {uploadMode === 'per-referent'
            ? 'Téléchargez une présentation différente pour chaque référent publique.'
            : 'Un seul PDF sera appliqué à l\'ensemble de vos référents publiques.'}
        </p>
      </div>

      {uploadMode === 'per-referent' ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {referents.map((referent) => (
            <ReferentPresentationUpload
              key={referent.id}
              companyId={company.id}
              referentId={referent.id}
              referentLabel={getReferentLabel(referent)}
              jwt={jwt}
              onUploadSuccess={handleUploadSuccess}
              onDeleteSuccess={handleDeleteSuccess}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h4 className="mb-2 font-semibold text-gray-800">Présentation commune</h4>
          <p className="mb-4 text-sm text-slate-600">
            Ce fichier sera dupliqué sur chacun de vos
            {' '}
            {referents.length}
            {' '}
            référent(s) publique(s) et utilisé en tête de leurs rapports respectifs.
          </p>
          {isLoadingShared || isBatchProcessing ? (
            <Spinner aria-label="Chargement de la présentation commune" />
          ) : (
            <FileUpload
              onFileSelect={sharedFileUpload.handleFiles}
              onFileDelete={sharedFileUpload.removeFile}
              acceptedFileTypes=".pdf"
              maxFileSize={MAX_FILE_SIZE}
              currentFile={sharedFileUpload.file}
            />
          )}
          {sharedFileUpload.file && !isBatchProcessing && (
            <button
              type="button"
              onClick={handlePreviewSharedPdf}
              className="mt-2 w-full rounded border-2 border-rgpd-blue bg-transparent px-2 py-1 font-semibold text-rgpd-blue hover:bg-rgpd-blue hover:text-white md:w-auto"
            >
              Prévisualiser le PDF
            </button>
          )}
        </div>
      )}

      <Popup
        isOpen={isPdfPreviewOpen}
        onClose={closePdfPreview}
        title="Présentation de l'entreprise pour les rapports"
        width="90vw"
        height="90vh"
      >
        <div className="h-full w-full">
          <iframe
            src={pdfPreviewUrl}
            className="h-full w-full border-none"
            title="PDF Preview"
          />
        </div>
      </Popup>
    </div>
  );
};

export default ReferentPresentationUploadList;
